import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import styles from "./Clients.module.css";
import * as XLSX from "xlsx-js-style";
import moment from "moment";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import Container from "@material-ui/core/Container";
import DeleteOutlineRoundedIcon from "@material-ui/icons/DeleteOutlineRounded";
import ListAltIcon from "@material-ui/icons/ListAlt";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import { Button, Typography } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { useSnackbar } from "react-simple-snackbar";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { deleteClient } from "../../actions/clientActions";
import { fetchAgentDueReport } from "../../api/index";
import { format } from "../../intl.js";
import { useMediaQuery } from "@material-ui/core";
// import clients from '../../clients.json'

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  // rowsPerPage === -1 is the "All" option — everything fits on one page.
  const totalPages = rowsPerPage > 0 ? Math.ceil(count / rowsPerPage) : 1;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, totalPages - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= totalPages - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= totalPages - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const useStyles2 = makeStyles((theme) => ({
  table: {
    minWidth: 500,
  },

  tablecell: {
    fontSize: "16px",
  },
  filterBar: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tableWrapper: {
    position: "relative",
  },
  tableContainer: {
    maxHeight: "calc(100vh - 320px)",
    overflow: "auto",
  },
  stickyHeaderCell: {
    backgroundColor: "#fff",
    zIndex: 2,
  },
  paginationBar: {
    borderTop: "1px solid rgba(224, 224, 224, 1)",
    overflowX: "auto",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    backgroundColor: "rgba(255, 255, 255, 0.4)",
    zIndex: 10,
  },
}));

// Outstanding direction, derived from the signed netBalance (amount) the
// backend already returns — positive means the agent owes ASN (receivable),
// negative means ASN owes the agent (payable), zero means settled. This
// mirrors receivableDue/payableDue without needing the backend to expose
// them separately.
const getStatusDetails = (amount) => {
  const value = amount || 0;
  if (value > 0) return "Agent owes ASN";
  if (value < 0) return "ASN owes Agent";
  return "No Outstanding";
};

const getAmountColor = (amount) => {
  const value = amount || 0;
  if (value > 0) return "#D32F2F"; // Red — Agent owes ASN
  if (value < 0) return "#F57C00"; // Orange — ASN owes Agent
  return "#2E7D32"; // Green — No outstanding
};

const Clients = ({ setOpen, setCurrentId }) => {
  const classes = useStyles2();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();

  // Agent Due Report: lifetime Outstanding Amount / Status for every agent
  // (no date range — Outstanding Due is a lifetime balance).
  // Pagination, search, and status filter are applied on the backend against
  // the full clients collection (not just the current page).
  const [report, setReport] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);
  // Debounced search so we query the DB after typing settles, not only
  // against whatever rows are currently rendered.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      try {
        setLoadingReport(true);
        const { data } = await fetchAgentDueReport({
          page,
          rowsPerPage,
          searchQuery: debouncedSearch,
          statusFilter,
        });
        if (cancelled) return;
        setReport(data.report || []);
        setTotalCount(
          typeof data.totalCount === "number" ? data.totalCount : (data.report || []).length
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Agent due report failed", error);
        openSnackbar("Unable to load agent due report");
      } finally {
        if (!cancelled) setLoadingReport(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const rows = report;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClientNameClick = (id) => {
    history.push(`/stats/${id}`);
  };
  const handleEdit = (selectedInvoice) => {
    setOpen((prevState) => !prevState);
    setCurrentId(selectedInvoice);
  };
  const handleDelete = (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this client?"
    );
    if (confirmDelete) {
      dispatch(deleteClient(id, openSnackbar));
    }
  };

  // Due List shows the magnitude only, with no +/- prefix — the Status
  // Details column and the amount's color already convey who owes whom.
  const formatAmount = (amount) => format(Math.abs(amount || 0));

  const tableStyle = {
    width: 160,
    fontSize: 14,
    cursor: "pointer",
    borderBottom: "none",
    padding: "8px",
    textAlign: "center",
  };
  const headerStyle = { borderBottom: "none", textAlign: "center" };

  const handleDownloadExcel = async () => {
    try {
      // Export every row matching the current filters (not just this page).
      const { data } = await fetchAgentDueReport({
        page: 0,
        rowsPerPage: -1,
        searchQuery: debouncedSearch,
        statusFilter,
      });
      const exportRows = data.report || [];
      if (!exportRows.length) {
        openSnackbar("No records to export");
        return;
      }

      const exportData = exportRows.map((row) => ({
        Agent: row.agentCode || "",
        "Outstanding Amount": Math.abs(row.amount || 0),
        "Status Details": getStatusDetails(row.amount),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const headerCellStyle = {
        fill: { fgColor: { rgb: "D3D3D3" } },
        font: { bold: true },
        alignment: { horizontal: "center" },
      };
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const headerCell = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[headerCell]) {
          worksheet[headerCell].s = headerCellStyle;
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Agent Due Report");

      const filename = `AgentDueReport_${moment().format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error("Agent due report export failed", error);
      openSnackbar("Unable to export agent due report");
    }
  };

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: isMobile ? "100%" : "85%" }}>
        <div className={classes.filterBar}>
          <FormControl style={{ minWidth: 160 }}>
            <InputLabel>Status Details</InputLabel>
            <Select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Agent owes ASN">Agent owes ASN</MenuItem>
              <MenuItem value="ASN owes Agent">ASN owes Agent</MenuItem>
              <MenuItem value="No Outstanding">No Outstanding</MenuItem>
            </Select>
          </FormControl>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleDownloadExcel}
          >
            Download Excel
          </Button>
        </div>

        <TextField
          id="search-bar"
          label="Search"
          variant="outlined"
          margin="normal"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
        />
        {/* <Typography variant="h2" align="center" gutterBottom style={{ marginTop: "2px", marginBottom: "10px" }}>
    Customers
      </Typography> */}
        <div className={classes.tableWrapper}>
        {loadingReport && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={64} thickness={4} />
          </div>
        )}
        <Paper elevation={0}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader className={classes.table} aria-label="custom pagination table">
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      ...headerStyle,
                      width: "10px",
                    }}
                    className={classes.stickyHeaderCell}
                  >
                    No
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Agent
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Outstanding Amount
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Status Details
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Statement
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Edit
                  </TableCell>
                  <TableCell
                    style={headerStyle}
                    className={classes.stickyHeaderCell}
                  >
                    Delete
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id} styel={{ cursor: "pointer" }}>
                    <TableCell style={{ ...tableStyle, width: "10px" }}>
                      {(rowsPerPage > 0 ? page * rowsPerPage : 0) + index + 1}
                    </TableCell>
                    <TableCell style={tableStyle}>{row.agentCode}</TableCell>
                    <TableCell
                      style={{
                        ...tableStyle,
                        color: row.amount !== undefined ? getAmountColor(row.amount) : undefined,
                        fontWeight: row.amount !== undefined ? 700 : undefined,
                      }}
                    >
                      {row.amount !== undefined ? formatAmount(row.amount) : "-"}
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableStyle,
                        color: row.amount !== undefined ? getAmountColor(row.amount) : undefined,
                        fontWeight: row.amount !== undefined ? 700 : undefined,
                      }}
                    >
                      {row.amount !== undefined ? getStatusDetails(row.amount) : "-"}
                    </TableCell>

                    <TableCell style={{ ...tableStyle, width: "10px" }}>
                      <IconButton onClick={() => handleClientNameClick(row.id)}>
                        <ListAltIcon style={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ ...tableStyle, width: "10px" }}>
                      <IconButton onClick={() => handleEdit(row.id)}>
                        <BorderColorIcon
                          style={{ width: "20px", height: "20px" }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ ...tableStyle, width: "10px" }}>
                      <IconButton onClick={() => handleDelete(row.id)}>
                        <DeleteOutlineRoundedIcon
                          style={{ width: "20px", height: "20px" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            className={classes.paginationBar}
            component="div"
            rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: { "aria-label": "rows per page" },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </Paper>
        </div>
      </Container>
    </div>
  );
};

export default Clients;
